import { useState } from 'react';
import PictureImage from '../Common/PictureImage';
import FAQSchema from '../../seo/FAQSchema';
import '../Homepage/FAQ.css';

/**
 * TikTok Shop–specific FAQs (not generic site FAQs). Schema id must not clash with homepage `faq-schema`.
 */
const TikTokShopFAQs = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      id: 1,
      question: 'How does Pick Pack Pro connect to my TikTok Shop for UK orders?',
      answer:
        'We integrate with TikTok Shop so orders flow into our WMS automatically: new orders import in near real time, inventory levels sync back to TikTok to reduce oversells and carrier tracking is pushed as soon as labels are generated—protecting your TikTok seller performance metrics.',
    },
    {
      id: 2,
      question: 'Can you handle TikTok Shop order spikes when a product video goes viral?',
      answer:
        'Yes. Viral catalogue traffic is the same operational problem as live spikes: we batch by SKU, add packing capacity and prioritise TikTok Shop lines that are trending so dispatch stays within your agreed cut-offs without manual firefighting.',
    },
    {
      id: 3,
      question: 'What dispatch cut-off do you offer for TikTok Shop orders in the UK?',
      answer:
        'We offer same-day dispatch for TikTok Shop orders received before the agreed cut-off (typically early afternoon, confirmed in your SLA). Late orders roll to the next working day with clear rules so you can set accurate handling times in Seller Centre.',
    },
    {
      id: 4,
      question: 'How quickly is tracking sent back to TikTok Shop after dispatch?',
      answer:
        'Tracking is submitted right after label generation and handover to the carrier, so buyers see updates promptly and TikTok’s systems receive proof of dispatch in line with marketplace expectations.',
    },
    {
      id: 5,
      question: 'Do you process TikTok Shop returns and refunds workflows?',
      answer:
        'Yes. Returns arrive at our Milton Keynes facility, are scanned and graded within 24–48 hours, then restocked, quarantined or disposed of per your SOPs—so TikTok Shop return reasons and inventory accuracy stay aligned.',
    },
    {
      id: 6,
      question: 'Can TikTok Shop share one inventory pool with Shopify and Amazon?',
      answer:
        'Yes. Many brands hold a single UK stock pool with us and fulfil TikTok Shop, Shopify, Amazon (FBM/SFP) and other channels from the same SKUs—reducing split inventory and stockouts while keeping channel-specific labels and packing rules.',
    },
    {
      id: 7,
      question: 'Do you support TikTok Shop bundles, kits and branded packaging?',
      answer:
        'Yes. We kit to your BOM, apply branded boxes or inserts where allowed and document pack-out so TikTok Shop listings, barcodes and carton contents stay consistent for audits and repeat orders.',
    },
    {
      id: 8,
      question: 'How fast can we go live on TikTok Shop fulfilment with Pick Pack Pro?',
      answer:
        'Most brands launch within a few working days after channel connection tests, SKU mapping, pack SOP sign-off and first inbound receipt—faster if master data and barcodes are already clean.',
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="faqs-section">
      <FAQSchema
        faqs={faqs.map(({ question, answer }) => ({ question, answer }))}
        id="tiktok-shop-faq-schema"
        nonce="ppp-2026-gtm-nonce"
      />
      <div className="faqs-container">
        <div className="faqs-header">
          <h2 className="faqs-title">TikTok Shop Fulfilment — FAQs</h2>
          <p className="faqs-description">
            Find answers to common questions about TikTok Shop fulfilment, integration, inventory management, dispatch,
            returns, and scaling your UK ecommerce business.
          </p>
        </div>

        <div className="faqs-content">
          <div className="faqs-list">
            {faqs.map((faq, index) => (
              <div key={faq.id} className={`faq-item ${openIndex === index ? 'faq-open' : ''}`}>
                <div className="faq-question-wrapper" onClick={() => toggleFAQ(index)}>
                  <h3 className="faq-question">{faq.question}</h3>
                  <button
                    type="button"
                    className={`faq-toggle ${openIndex === index ? 'faq-toggle-open' : ''}`}
                    aria-label={openIndex === index ? 'Close answer' : 'Open answer'}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFAQ(index);
                    }}
                  >
                    <svg width="32" height="32" viewBox="0 0 20 20" fill="none">
                      {openIndex === index ? (
                        <path d="M5 10H15" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                      ) : (
                        <>
                          <path d="M10 5V15" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                          <path d="M5 10H15" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
                <div className={`faq-answer ${openIndex === index ? 'faq-answer-open' : ''}`}>
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="faqs-image-wrapper">
            <PictureImage
              src="/Images/merchent/FAQ_image.webp"
              alt="TikTok Shop fulfilment UK — Frequently Asked Questions"
              className="faqs-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default TikTokShopFAQs;
